import { motion } from 'framer-motion'
import {
  Search, Book, Video, FileText,
  ChevronRight, HelpCircle, ArrowRight, Mail,
  Phone, ExternalLink, Rocket, Wifi, Shield,
  Settings, Building2, CreditCard
} from 'lucide-react'
import { Link } from '../i18n/Link'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import SEO, { schemas } from '../components/SEO'
import { useApi } from '../hooks/useApi'
import { helpApi } from '../lib/api'

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Rocket, Wifi, Shield, Settings, Building2, CreditCard,
}

const colorMap: Record<string, string> = {
  'getting-started': 'from-blue-500 to-cyan-500',
  'connection-issues': 'from-orange-500 to-red-500',
  'security': 'from-green-500 to-emerald-500',
  'advanced-settings': 'from-purple-500 to-pink-500',
  'enterprise': 'from-indigo-500 to-blue-500',
  'billing': 'from-yellow-500 to-orange-500',
}

export default function Help() {
  const { t } = useTranslation(['help', 'common'])
  const [searchQuery, setSearchQuery] = useState('')
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const { data: categories } = useApi(() => helpApi.getCategories(), [])
  const { data: popularArticles } = useApi(() => helpApi.getPopularArticles(), [])
  const { data: faqs } = useApi(() => helpApi.getFAQ(), [])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      
      className="pt-32 pb-24"
    >
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonical="/help"
        structuredData={schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.help'), url: '/help' }])}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-4">
            <HelpCircle className="w-4 h-4" />
            {t('eyebrow')}
          </span>
          <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
            {t('heroTitle')}{' '}
            <span className="text-gradient">{t('heroTitleHighlight')}</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
            {t('heroText')}
          </p>

          {/* Search */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-500" />
              <input
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-14 pr-4 py-5 glass rounded-2xl text-white text-lg placeholder-gray-500 focus:outline-hidden focus:border-primary transition-colors"
              />
            </div>
          </div>
        </motion.div>

        {/* Categories */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16"
        >
          {(categories || []).map((category, index) => {
            const IconComp = iconMap[category.icon || ''] || HelpCircle
            const color = colorMap[category.slug] || 'from-primary to-secondary'
            return (
              <motion.div
                key={category.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-2xl p-6 group cursor-pointer hover:bg-white/10 transition-all"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <IconComp className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-primary transition-colors">
                  {category.name}
                </h3>
                <p className="text-gray-400 text-sm mb-4">{category.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-sm">{t('articleCount', { n: category._count.articles })}</span>
                  <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-primary transition-colors" />
                </div>
              </motion.div>
            )
          })}
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Popular Articles */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6 mb-8"
            >
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                {t('popularTitle')}
              </h2>
              <div className="space-y-3">
                {(popularArticles || []).map((article, index) => (
                  <div
                    key={article.title}
                    className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500 font-mono">{String(index + 1).padStart(2, '0')}</span>
                      <span className="text-white group-hover:text-primary transition-colors">{article.title}</span>
                    </div>
                    <span className="text-gray-500 text-sm">{t('viewCount', { n: article.viewCount })}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* FAQs */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6"
            >
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-primary" />
                {t('faqTitle')}
              </h2>
              <div className="space-y-3">
                {(faqs || []).map((faq, index) => (
                  <div key={index} className="border border-white/10 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors"
                    >
                      <span className="text-white font-medium">{faq.question}</span>
                      <ChevronRight className={`w-5 h-5 text-gray-500 transition-transform ${openFaq === index ? 'rotate-90' : ''}`} />
                    </button>
                    {openFaq === index && (
                      <div className="px-4 pb-4">
                        <p className="text-gray-400">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Support */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6"
            >
              <h3 className="text-lg font-semibold text-white mb-4">{t('support.title')}</h3>
              <p className="text-gray-400 text-sm mb-4">
                {t('support.text')}
              </p>
              <div className="space-y-3">
                <a
                  href="mailto:destek@cmdesk.help"
                  className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-primary/20 transition-colors group"
                >
                  <Mail className="w-5 h-5 text-gray-400 group-hover:text-primary" />
                  <span className="text-white group-hover:text-primary transition-colors">{t('support.email')}</span>
                </a>
                <a
                  href="tel:+908501234567"
                  className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-primary/20 transition-colors group"
                >
                  <Phone className="w-5 h-5 text-gray-400 group-hover:text-primary" />
                  <span className="text-white group-hover:text-primary transition-colors">{t('support.phone')}</span>
                </a>
              </div>
            </motion.div>

            {/* Video Tutorials */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6"
            >
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Video className="w-5 h-5 text-primary" />
                {t('videos.title')}
              </h3>
              <div className="space-y-3">
                <div className="aspect-video rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center cursor-pointer group">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-primary transition-colors">
                    <Video className="w-5 h-5 text-white" />
                  </div>
                </div>
                <p className="text-sm text-gray-400">{t('videos.intro')}</p>
              </div>
              <Link
                to="#"
                className="flex items-center gap-2 mt-4 text-primary hover:text-primary-dark text-sm font-medium"
              >
                {t('videos.all')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            {/* Documentation */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6"
            >
              <h3 className="text-lg font-semibold text-white mb-4">{t('docs.title')}</h3>
              <p className="text-gray-400 text-sm mb-4">
                {t('docs.text')}
              </p>
              <Link
                to="/docs"
                className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-primary/20 rounded-lg text-white text-sm transition-colors group"
              >
                <Book className="w-4 h-4 text-primary" />
                <span className="group-hover:text-primary transition-colors">{t('docs.link')}</span>
                <ExternalLink className="w-3 h-3 ml-auto text-gray-500" />
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
